/**
 * Kiosk analytics policy — product analytics is FORBIDDEN on player surfaces.
 *
 * Why (this is a decision, not an oversight):
 * - There is nobody at a venue screen to answer a consent banner. A ceiling
 *   display in a supermarket cannot give informed consent, so the lawful basis
 *   for behavioural analytics simply does not exist there. Consent on a kiosk
 *   is structurally unobtainable, not merely inconvenient.
 * - The people in front of that screen are passers-by, not account holders.
 *   Nothing about them is ours to measure.
 * - Everything the business actually needs from a player is *operational*:
 *   heartbeats, play logs, device events, crash reports. Those run on
 *   contractual necessity / legitimate interest with the operator who paired
 *   the device — an entirely different, and defensible, story. They keep
 *   working and are unaffected by this policy.
 * - The analytics questions a player could answer ("did this venue actually
 *   play music?") are answered better server-side anyway, from telemetry the
 *   device already sends. See `device.playback_started`.
 *
 * So: Sentry crash reporting on a player = yes. PostHog on a player = never.
 *
 * This module is the enforcement point rather than a paragraph in a doc:
 * `web/index.ts` refuses to initialise PostHog on a kiosk path and drops every
 * `track()` / `trackPageView()` / `setUser()` made from one, and `mobile/index.ts`
 * does the same once the shell reports kiosk mode. A `capture()` added to a
 * kiosk screen by mistake is therefore inert by construction.
 *
 * Enforcement is always fail-SAFE: blocked calls no-op and (in dev) warn once.
 * Nothing here may ever throw — a kiosk that stopped playing because of an
 * analytics guard would be a far worse bug than the blindness it prevents.
 */

export const KIOSK_ANALYTICS_POLICY =
  "Product analytics is disabled on player/kiosk surfaces: nobody at a venue screen can consent, and the audience is a passer-by rather than an account holder. Operational telemetry (heartbeats, play logs, device events, crash reports) is unaffected.";

/** Web routes that render the kiosk player (also used by the consent banner). */
export function isPlayerKioskPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname === "/player" || pathname.startsWith("/player/");
}

export interface KioskSurfaceContext {
  /** Web: the current location pathname. */
  pathname?: string | null;
  /** Native shells: true once the app is running as a player. */
  kioskMode?: boolean;
}

/** True when the current surface is a player/kiosk surface. */
export function isKioskSurface(ctx: KioskSurfaceContext): boolean {
  if (ctx.kioskMode === true) return true;
  return isPlayerKioskPath(ctx.pathname);
}

/** The one predicate every product-analytics entry point must consult. */
export function productAnalyticsAllowedOnSurface(
  ctx: KioskSurfaceContext,
): boolean {
  return !isKioskSurface(ctx);
}

const warned = new Set<string>();

/**
 * Dev-only, once per event name: tell whoever just added a kiosk capture that
 * it was dropped and why. Silent in production, and never throws.
 */
export function warnKioskCaptureBlocked(what: string): void {
  try {
    if (warned.has(what)) return;
    warned.add(what);
    if (warned.size > 50) warned.clear();
    const isProd =
      typeof process !== "undefined" &&
      process.env?.NODE_ENV === "production";
    if (isProd) return;
    console.warn(
      `[observability] "${what}" was dropped: ${KIOSK_ANALYTICS_POLICY}`,
    );
  } catch {
    // never let a warning break a player
  }
}

/** Test seam. */
export function resetKioskWarnings(): void {
  warned.clear();
}
